/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react'
import { PortableText, type PortableTextComponent } from '@portabletext/react'
// import { RoomSettingsContext } from '../+RoomSettingsContext'
import Footer from '../../../components/Footer'
import Wrap from '../../../components/Wrap'
import {
  Box,
  Flex,
  Heading,
  Img,
  Text,
  OrderedList,
  UnorderedList,
  ListItem
} from '@chakra-ui/react'
import { client as SanityClient } from '../../../renderer/sanity'
import { format } from 'date-fns'
import NoteLink from '../../../components/NoteLink'
import { SanityImage } from 'sanity-image'
import { usePageContext } from 'vike-react/usePageContext'
interface PortableTextObject {
  value: any
  children: any
}
const textComponents: any = {
  types: {
    image: ({ value }: PortableTextObject) => {
      return (
        <Box my="40px">
          <Img
            as={SanityImage}
            id={value.asset._ref}
            projectId={SanityClient.config().projectId}
            dataset={SanityClient.config().dataset}
            width={750}
            alt={value.alt}
            mx="auto"
            sizes="(min-width: 750px) 750px, 100vw"
          />
          {value.caption !== '' && (
            <Text as="span" variant="caption" mx="auto">
              {value.caption}
            </Text>
          )}
        </Box>
      )
    }
  },
  list: {
    bullet: ({ children }: PortableTextObject) => (
      <UnorderedList>{children}</UnorderedList>
    ),
    number: ({ children }: PortableTextObject) => (
      <OrderedList>{children}</OrderedList>
    )
  },
  listItem: {
    bullet: ({ children }: PortableTextObject) => (
      <ListItem>{children}</ListItem>
    ),
    number: ({ children }: PortableTextObject) => (
      <ListItem>{children}</ListItem>
    )
  },
  marks: {
    link: ({ value, children }: PortableTextObject) => {
      return (
        <NoteLink href={value.href} target={value.blank}>
          {children}
        </NoteLink>
      )
    }
  },
  block: {
    normal: ({ children }: PortableTextObject) => {
      return (
        <Text
          as="p"
          fontSize="lg"
          letterSpacing="-0.005em"
          fontWeight="400"
          lineHeight={1.75}
          mb={4}
        >
          {children}
        </Text>
      )
    },
    blockquote: ({ children }: PortableTextObject) => {
      return (
        <Heading
          as="p"
          fontSize="32px"
          letterSpacing="-0.05em"
          fontWeight="600"
          lineHeight={1.1}
          my="2em"
          textAlign="center"
          backgroundClip="text"
          backgroundImage="linear-gradient(148deg, rgba(253,89,1,1) 0%, rgba(247,129,4,1) 15%, rgba(250,171,54,1) 30%, rgba(36,158,160,1) 70%, rgba(0,128,131,1) 85%, rgba(0,95,96,1) 100%);"
        >
          {children}
        </Heading>
      )
    },
    h2: ({ children }: PortableTextObject) => {
      return (
        <Heading as="h2" size="xl" mt="2em">
          {children}
        </Heading>
      )
    },
    h3: ({ children }: PortableTextObject) => {
      return (
        <Heading as="h3" size="lg" mt="2em">
          {children}
        </Heading>
      )
    },
    h4: ({ children }: PortableTextObject) => {
      return (
        <Heading as="h4" size="sm" mt="2em">
          {children}
        </Heading>
      )
    },
    h5: ({ children }: PortableTextObject) => {
      return (
        <Heading as="h5" size="xl" mt="2em">
          {children}
        </Heading>
      )
    }
  }
}

const Page: React.FC = () => {
  const pageContext = usePageContext() as any
  const pageProps = pageContext.pageProps
  const post: any = pageProps.posts[0]
  const formatedDate = format(new Date(post.publishedAt), 'PPP')

  return (
    <>
      <Wrap
        bottomHR={false}
        justifyContent="center"
        minH="calc(100vh - 45px - 58px)"
        pt="100px"
      >
        <Flex flexDir="column" w="100%">
          <Flex
            flexDir="column"
            mt="20px"
            w={{ base: 'calc(100% - 20px)', md: '80%' }}
            maxW="750px"
            mx="auto"
          >
            <Heading as="h1" mb="0">
              {post.title}
            </Heading>
            <Text variant="blogDate" mt="0">
              {formatedDate} - {post.author.name}
            </Text>
          </Flex>
          <Flex flexDir="column" w="100%" mb="20px">
            <Box
              w="100%"
              pos="relative"
              sx={{
                '.bend': {
                  position: 'absolute',
                  bg: 'white',
                  bottom: '0px',
                  right: '0px',
                  borderStyle: 'solid',
                  h: 'auto',
                  aspectRatio: '1/1',
                  w: '5%',
                  boxShadow: '0px 2px 0px 0px white, 2px 0px 0px 0px white',
                  transition: 'all 0.5s',
                  zIndex: 2,
                  overflow: 'hidden',
                  '&:after': {
                    pointerEvents: 'none',
                    color:
                      typeof post.mainImage.cornerColor !== 'undefined'
                        ? post.mainImage.cornerColor
                        : 'yellow.300',
                    background:
                      'linear-gradient(135deg, currentcolor calc(50% - 1px), rgba(232,232,232,1) 50%, rgba(255,255,255,0) 50.2%, rgba(255,255,255,0) 100%)',
                    content: '""',
                    display: 'block',
                    pos: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                  },
                  button: {
                    pos: 'absolute',
                    right: '20px',
                    bottom: '20px'
                  }
                }
              }}
            >
              <Box className="bend" />
              <Img
                as={SanityImage}
                id={post.mainImage.asset._ref}
                aspectRatio="5/2"
                projectId={SanityClient.config().projectId}
                dataset={SanityClient.config().dataset}
                width={1200}
                sizes="(min-width: 1200px) 1200px, 100vw"
                alt={post.mainImage.alt}
                w="100%"
                objectFit="cover"
              />
            </Box>
            <Text as="span" variant="caption" w="100%" textAlign="left">
              <span
                dangerouslySetInnerHTML={{ __html: post.mainImage.attribution }}
              />
            </Text>
          </Flex>
          <hr />
          <Flex
            flexDir="column"
            mt="20px"
            gap="20px"
            w={{ base: 'calc(100% - 20px)', md: '80%' }}
            maxW="750px"
            mx="auto"
          >
            <Box>
              <PortableText value={post.body} components={textComponents} />
            </Box>
          </Flex>
        </Flex>
      </Wrap>
      <Footer />
    </>
  )
}
export { Page }
